import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { headerSty, contentSty } from "../table-styles";
import { useMutation } from "@tanstack/react-query";
import { apiGetEmploymentsByFacility } from "../../api/employment";
import { useNotificationService } from "../notification";
import { EmploymentDetailsProps } from "../../interface/employment";
import { formatDate, formatTextType, limitToSeven } from "../../utils/url-id";

export default function DashboardTable() {
  const theme = useTheme();
  const headStyles = { ...headerSty, color: theme.palette.neutral.main };
  const contentStyles = { ...contentSty };

  const [openRow, setOpenRow] = React.useState<number | null>(null); // Track which row is open
  const navigate = useNavigate();
  const { showNotification } = useNotificationService();
  const [employment_list, setEmployment_list] = React.useState<
    EmploymentDetailsProps[]
  >([]);

  React.useEffect(() => {
    mutation.mutate();
  }, []);

  const handleToggleOpen = (rowId: number) => {
    setOpenRow((prevOpenRow) => (prevOpenRow === rowId ? null : rowId));
  };

  const mutation = useMutation({
    mutationFn: () => apiGetEmploymentsByFacility("", ""),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        if (data?.data?.data?.length > 0) {
          const firstFourData = data.data.data?.slice(0, 4);
          setEmployment_list(firstFourData);
        } else {
          setEmployment_list([]);
        }
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });

  return (
    <TableContainer
      component={Paper}
      sx={{
        my: 1,
        background: theme.palette.common.white,
        borderRadius: "16px",
        border: 0,
        boxShadow: "none",
        minHeight: 250,
      }}
    >
      <Table
        sx={{
          minWidth: 650,
          boxShadow: "none",
          my: 2,
          paddingRight: 2,
          border: 0,
        }}
        aria-label="simple table"
      >
        <TableHead sx={{ paddingX: 2, border: 0 }}>
          <TableRow>
            <TableCell sx={{ ...headStyles, width: "12%" }}>
              Employee ID
            </TableCell>
            <TableCell sx={{ ...headStyles }}>Job Title</TableCell>
            <TableCell sx={{ ...headStyles }}>Department</TableCell>
            <TableCell sx={{ ...headStyles }}>Start Date</TableCell>
            <TableCell sx={{ ...headStyles }}>End Date</TableCell>
            <TableCell sx={{ ...headStyles }}>Emp. Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {employment_list?.map((row: any, index: number) => (
            <TableRow
              onClick={() => navigate(`/employment/${row?._id}`)}
              key={row?._id}
              // sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell sx={contentStyles}>
                {row?.employee_id ?? limitToSeven(row?._id)}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {row?.job_info?.job_title}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {row?.job_info?.department ?? "-"}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {formatDate(row?.employment_details?.start_date)}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {formatDate(row?.employment_details?.end_date)}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                <EmploymentEmpStatus status={formatTextType(row.status)} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {employment_list?.length === 0 && !mutation.isPending && (
        <Box
          sx={{
            height: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: theme.palette.neutral.main }}
          >
            No Employee Available.
          </Typography>
        </Box>
      )}
      {mutation.isPending && (
        <Box sx={{ width: "96%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
        </Box>
      )}
    </TableContainer>
  );
}

export const EmploymentEmpStatus: React.FC<{
  status: "pending" | "inactive" | "active" | string;
}> = ({ status }) => {
  const theme = useTheme();

  const getStatusStyles = (status: string) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return {
          color: theme.palette.warning.main,
          backgroundColor: "#ffac336b",
        };
      case "inactive":
        return {
          color: theme.palette.error.main,
          backgroundColor: "#f6685e6b",
        };

      case "active":
        return {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.lightBlue.main,
        };
      default:
        return {
          color: theme.palette.success.main,
          backgroundColor: theme.palette.success.light,
        };
    }
  };

  const statusStyles = getStatusStyles(status);

  return (
    <Stack
      direction="row"
      justifyContent="center"
      sx={{ width: 100, mx: "auto" }}
    >
      <Box
        sx={{
          marginX: 1,
          paddingY: 1,
          paddingX: 3,
          borderRadius: "3px",
          fontSize: 12,
          fontFamily: "Lexend",
          textTransform: "uppercase",
          ...statusStyles,
        }}
      >
        {status}
      </Box>
    </Stack>
  );
};
